import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Copy, Loader2, Monitor, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useBoardShares, useCosmosUsers, useSaveBoardShares, type BoardShares, type Dashboard } from '@/api/boards';
import { dashboardDisplayPath, dashboardPath } from '@/lib/routes';

/**
 * Share a dashboard: everyone in Cosmos (view / edit) and/or named people. Only the owner can
 * change sharing; links only work for people it's shared with (the API checks every request).
 */
export function ShareDialog({ open, onOpenChange, dashboard, isOwner }: { open: boolean; onOpenChange: (o: boolean) => void; dashboard: Dashboard; isOwner: boolean }) {
  const shares = useBoardShares(dashboard.id, open);
  const users = useCosmosUsers(open && isOwner);
  const save = useSaveBoardShares();
  const [everyone, setEveryone] = useState<BoardShares['everyone']>('none');
  const [people, setPeople] = useState<BoardShares['users']>([]);
  const [pick, setPick] = useState('');

  useEffect(() => { if (shares.data) { setEveryone(shares.data.everyone); setPeople(shares.data.users); } }, [shares.data]);
  const available = useMemo(() => (users.data?.users ?? []).filter((u) => !people.some((p) => p.userId === u.id)), [users.data, people]);

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const copy = (path: string, what: string) => { void navigator.clipboard.writeText(`${origin}${path}`).then(() => toast.success(`${what} copied`)); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Share “{dashboard.name}”</DialogTitle></DialogHeader>
        {shares.isLoading ? <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div> : (
          <div className="space-y-4 text-sm">
            <div>
              <div className="mb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Everyone in Cosmos</div>
              <select value={everyone} disabled={!isOwner} onChange={(e) => setEveryone(e.target.value as BoardShares['everyone'])}
                className="h-8 w-full rounded-md border border-border bg-background px-2 text-sm text-foreground">
                <option value="none">Not shared (only people below)</option>
                <option value="view">Can view</option>
                <option value="edit">Can edit</option>
              </select>
            </div>
            <div>
              <div className="mb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">People</div>
              {people.length === 0 && <div className="text-xs text-muted-foreground">Nobody yet.</div>}
              <ul className="space-y-1">
                {people.map((p) => (
                  <li key={p.userId} className="flex items-center gap-2">
                    <span className="min-w-0 flex-1 truncate">{p.name ?? p.email ?? p.userId} <span className="text-xs text-muted-foreground">{p.email}</span></span>
                    <select value={p.permission} disabled={!isOwner} onChange={(e) => setPeople((xs) => xs.map((x) => (x.userId === p.userId ? { ...x, permission: e.target.value as 'view' | 'edit' } : x)))}
                      className="h-7 rounded-md border border-border bg-background px-1 text-xs text-foreground">
                      <option value="view">Can view</option>
                      <option value="edit">Can edit</option>
                    </select>
                    {isOwner && <button type="button" onClick={() => setPeople((xs) => xs.filter((x) => x.userId !== p.userId))} className="text-muted-foreground hover:text-destructive" aria-label="Remove"><X className="h-3.5 w-3.5" /></button>}
                  </li>
                ))}
              </ul>
              {isOwner && (
                <div className="mt-2 flex gap-2">
                  <select value={pick} onChange={(e) => setPick(e.target.value)} className="h-8 min-w-0 flex-1 rounded-md border border-border bg-background px-2 text-sm text-foreground">
                    <option value="">Add a person…</option>
                    {available.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
                  </select>
                  <Button size="sm" variant="outline" disabled={!pick} onClick={() => {
                    const u = available.find((x) => x.id === pick);
                    if (u) setPeople((xs) => [...xs, { userId: u.id, permission: 'view', name: u.name, email: u.email }]);
                    setPick('');
                  }}>Add</Button>
                </div>
              )}
              {!isOwner && <div className="mt-2 text-xs text-muted-foreground">Only {dashboard.ownerName} can change who this is shared with.</div>}
            </div>
            <div className="flex flex-wrap gap-2 border-t border-divider pt-3">
              <Button size="sm" variant="outline" onClick={() => copy(dashboardPath(dashboard.id), 'Link')}><Copy className="h-3.5 w-3.5" /> Copy link</Button>
              <Button size="sm" variant="outline" onClick={() => copy(dashboardDisplayPath(dashboard.id), 'Display link')}><Monitor className="h-3.5 w-3.5" /> Copy wall-display link</Button>
            </div>
            {isOwner && (
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                <Button size="sm" disabled={save.isPending} onClick={() => save.mutate(
                  { id: dashboard.id, everyone, users: people.map((p) => ({ userId: p.userId, permission: p.permission })) },
                  { onSuccess: () => { toast.success('Sharing updated'); onOpenChange(false); }, onError: (e) => toast.error(e instanceof Error ? e.message : 'Failed to save') },
                )}>{save.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Save</Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
